import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface NavItemProps {
  title: string;
  titleEn: string;
  path?: string;
  category?: string;
  children?: {
    title: string;
    titleEn: string;
    description?: string;
    path?: string;
    subCategory?: string;
  }[];
}

const navItems: NavItemProps[] = [
  {
    title: "Trang chủ",
    titleEn: "Home",
    path: "/",
  },
  {
    title: "Sống Thông Minh",
    titleEn: "Smart Living",
    category: "smart-living",
    path: "/articles",
    children: [
      {
        title: "Tài chính cá nhân",
        titleEn: "Money & Finance",
        subCategory: "finance",
      },
      {
        title: "Mua sắm thông minh",
        titleEn: "Smart Shopping",
        subCategory: "shopping",
      },
      {
        title: "Mẹo gia đình",
        titleEn: "Home Hacks",
        subCategory: "home-hacks",
      },
    ],
  },
  {
    title: "Làm Đẹp & Phong Cách",
    titleEn: "Glow & Beauty",
    category: "beauty",
    path: "/articles",
    children: [
      {
        title: "Làm đẹp",
        titleEn: "Beauty",
        subCategory: "skincare",
      },
      {
        title: "Thời trang",
        titleEn: "Fashion & Style",
        subCategory: "fashion",
      },
      {
        title: "Chăm sóc bản thân",
        titleEn: "Self-Care",
        subCategory: "self-care",
      },
    ],
  },
  {
    title: "Sức Khỏe & Gia Đình",
    titleEn: "Health & Family",
    category: "health",
    path: "/articles",
    children: [
      {
        title: "Sống khỏe",
        titleEn: "Healthy Living",
        subCategory: "healthy-living",
      },
      {
        title: "Mẹ & Bé",
        titleEn: "Mom & Kids",
        subCategory: "mom-kids",
      },
      {
        title: "Cân bằng công việc",
        titleEn: "Work-Life Balance",
        subCategory: "work-life",
      },
    ],
  },
  {
    title: "Mua Sắm & Đánh Giá",
    titleEn: "Shop & Reviews",
    category: "shop",
    path: "/articles",
    children: [
      {
        title: "Đánh giá sản phẩm",
        titleEn: "Product Reviews",
        subCategory: "reviews",
      },
      {
        title: "Mã giảm giá",
        titleEn: "Deals & Coupons",
        subCategory: "deals",
      },
      {
        title: "Lựa chọn hàng đầu",
        titleEn: "Best Picks",
        subCategory: "best-picks",
      },
    ],
  },
  {
    title: "Bài Viết & Câu Chuyện",
    titleEn: "Blog & Stories",
    category: "blog",
    path: "/articles",
    children: [
      {
        title: "Chủ đề xu hướng",
        titleEn: "Trending Topics",
        subCategory: "trending",
      },
      {
        title: "Phát triển cá nhân",
        titleEn: "Personal Growth",
        subCategory: "growth",
      },
      {
        title: "Phụ nữ truyền cảm hứng",
        titleEn: "Inspiring Women",
        subCategory: "inspiring",
      },
    ],
  },
  {
    title: "Giới thiệu",
    titleEn: "About Us",
    path: "/about",
  },
  {
    title: "Liên hệ",
    titleEn: "Contact",
    path: "/contact",
  },
];

const NavItem = ({ item }: { item: NavItemProps }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getItemPath = () => {
    if (!item.category) return item.path;
    return `${item.path}?category=${item.category}`;
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        to={getItemPath() || "/"}
        className="flex items-center gap-1 px-3 py-5 text-white hover:text-gold transition-colors duration-300 whitespace-nowrap"
      >
        <span>{item.titleEn}</span>
        {item.children && <ChevronDown className="h-4 w-4" />}
      </Link>

      {item.children && isHovered && (
        <div className="absolute left-0 top-full min-w-[200px] navy-glass rounded-md overflow-hidden shadow-lg z-50 dropdown-animation">
          <div className="p-2">
            {item.children.map((child) => (
              <Link
                key={child.subCategory}
                to={`${item.path}?category=${item.category}&sub_category=${child.subCategory}`}
                className="block px-4 py-3 hover:bg-navy-light rounded-md transition-colors duration-200"
              >
                <div className="text-white font-medium">{child.titleEn}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileNavItem = ({
  item,
  isOpen,
  toggleOpen,
}: {
  item: NavItemProps;
  isOpen: boolean;
  toggleOpen: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getItemPath = () => {
    if (!item.category) return item.path;
    return `${item.path}?category=${item.category}`;
  };

  return (
    <div className="border-b border-navy-light/30 last:border-0">
      <div
        className="flex items-center justify-between py-4 px-6 text-white"
        onClick={() => item.children && setIsExpanded(!isExpanded)}
      >
        <Link
          to={getItemPath() || "/"}
          className="font-medium"
          onClick={(e) => {
            if (item.children) {
              e.preventDefault();
            } else {
              toggleOpen();
            }
          }}
        >
          {item.titleEn}
        </Link>
        {item.children && (
          <ChevronDown
            className={`h-5 w-5 transition-transform duration-300 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        )}
      </div>

      {item.children && isExpanded && (
        <div className="bg-navy-light/30 py-2">
          {item.children.map((child) => (
            <Link
              key={child.subCategory}
              to={`${item.path}?category=${item.category}&sub_category=${child.subCategory}`}
              className="block py-3 px-8 text-white/90 hover:text-gold"
              onClick={toggleOpen}
            >
              {child.titleEn}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 flex flex-col navy-gradient shadow-md">
        <div className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-6 w-full max-w-full">
          {/* Logo */}
          <Link
            to="/"
            className="text-gold font-playfair text-2xl sm:text-3xl font-semibold"
          >
            GlowHer<span className="text-white">.Life</span>
          </Link>

          {/* Desktop Navigation */}
          {!isMobile ? (
            <nav className="flex items-center">
              {navItems.map((item) => (
                <NavItem key={item.category || item.path} item={item} />
              ))}
            </nav>
          ) : (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-gold transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          )}
        </div>

        {/* Date display as part of header */}
        <div className="bg-gray-100 text-navy text-center text-xs py-1">
          {currentDate}
        </div>
      </header>

      {/* Mobile menu */}
      {isMobile && (
        <div
          className={`fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300 ${
            isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setIsOpen(false)}
        >
          <div
            className={`fixed top-0 right-0 h-full w-3/4 max-w-sm navy-gradient shadow-xl transition-transform duration-300 ease-in-out transform ${
              isOpen ? "translate-x-0" : "translate-x-full"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center p-4 border-b border-navy-light/30">
                <Link
                  to="/"
                  className="text-gold font-playfair text-xl font-semibold"
                  onClick={() => setIsOpen(false)}
                >
                  GlowHer<span className="text-white">.Life</span>
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:text-gold transition-colors"
                  aria-label="Close menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2">
                {navItems.map((item) => (
                  <MobileNavItem
                    key={item.category || item.path}
                    item={item}
                    isOpen={isOpen}
                    toggleOpen={() => setIsOpen(false)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
